import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Switch, message, Card, Space, Divider, Upload } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import api from '../api/axios';

const { Option } = Select;

function StudentAppsManagement() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [form] = Form.useForm();
  const [iconUrl, setIconUrl] = useState('');

  // 获取应用列表
  const fetchApps = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/student-apps');
      setApps(response.data);
    } catch (error) {
      message.error('获取应用列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApps();
  }, []);

  // 打开新增/编辑弹窗
  const handleOpenModal = (app = null) => {
    setEditingApp(app);
    setIconUrl(app?.app_icon || '');
    if (app) {
      form.setFieldsValue(app);
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  // 关闭弹窗
  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingApp(null);
    setIconUrl('');
    form.resetFields();
  };

  // 上传图标
  const handleIconUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/admin/student-apps/upload-icon', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setIconUrl(response.data.url);
      form.setFieldsValue({ app_icon: response.data.url });
      message.success('图标上传成功');
    } catch (error) {
      message.error('图标上传失败');
    }
    
    return false;
  };

  // 保存应用
  const handleSaveApp = async (values) => {
    // 确保app_icon只保存URL字符串，不保存Upload组件的状态对象
    if (values.app_icon && typeof values.app_icon === 'object') {
      values.app_icon = iconUrl || '';
    }
    
    try {
      if (editingApp) {
        await api.put(`/admin/student-apps/${editingApp.id}`, values);
        message.success('更新应用成功');
      } else {
        await api.post('/admin/student-apps', values);
        message.success('添加应用成功');
      }
      handleCloseModal();
      fetchApps();
    } catch (error) {
      message.error('保存应用失败');
    }
  };

  // 删除应用
  const handleDeleteApp = async (app) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除应用"${app.app_name}"吗？`,
      onOk: async () => {
        try {
          await api.delete(`/admin/student-apps/${app.id}`);
          message.success('删除应用成功');
          fetchApps();
        } catch (error) {
          message.error('删除应用失败');
        }
      }
    });
  };

  // 获取应用类型显示
  const getAppTypeText = (type) => {
    return type === 'theme' ? '主题应用' : '分类应用';
  };

  // 表格列
  const columns = [
    {
      title: '应用键',
      dataIndex: 'app_key',
      key: 'app_key',
      width: 150,
    },
    {
      title: '应用名称',
      dataIndex: 'app_name',
      key: 'app_name',
      width: 150,
    },
    {
      title: '应用类型',
      dataIndex: 'app_type',
      key: 'app_type',
      width: 120,
      render: (text) => getAppTypeText(text),
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
    },
    {
      title: '访问URL',
      dataIndex: 'app_url',
      key: 'app_url',
      ellipsis: true,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '是否启用',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (text, record) => (
        <Switch
          checked={text}
          onChange={async (checked) => {
            try {
              await api.put(`/admin/student-apps/${record.id}`, { ...record, is_active: checked });
              message.success('更新成功');
              fetchApps();
            } catch (error) {
              message.error('更新失败');
            }
          }}
        />
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (text, record) => (
        <Space>
          <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleOpenModal(record)}
        >
          编辑
        </Button>
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteApp(record)}
        >
          删除
        </Button>
      </Space>
      ),
    },
  ];

  // 按类型分组显示
  const themeApps = apps.filter(app => app.app_type === 'theme');
  const categoryApps = apps.filter(app => app.app_type === 'category');

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>学生应用管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
          添加应用
        </Button>
      </div>

      <Divider orientation="left">主题应用</Divider>
      <Card style={{ marginBottom: 16 }}>
        <Table
          columns={columns}
          dataSource={themeApps}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      <Divider orientation="left">分类应用</Divider>
      <Card>
        <Table
          columns={columns}
          dataSource={categoryApps}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      {/* 新增/编辑应用弹窗 */}
      <Modal
        title={editingApp ? '编辑应用' : '添加应用'}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={handleCloseModal}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveApp}
        >
          {!editingApp && (
            <Form.Item
              label="应用键"
              name="app_key"
            rules={[
              { required: true, message: '请输入应用键' },
              { pattern: /^[a-z][a-z0-9_-]*$/, message: '应用键只能以小写字母开头，包含字母、数字、下划线和短横线' }
            ]}
          >
            <Input placeholder="例如: attendance" />
          </Form.Item>
          )}
          
          <Form.Item
          label="应用名称"
          name="app_name"
            rules={[{ required: true, message: '请输入应用名称' }]}
          >
            <Input placeholder="例如: 考勤" />
          </Form.Item>

          <Form.Item
          label="应用类型"
          name="app_type"
            rules={[{ required: true, message: '请选择应用类型' }]}
          >
            <Select placeholder="请选择应用类型">
              <Option value="theme">主题应用</Option>
              <Option value="category">分类应用</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="应用图标"
            name="app_icon"
          >
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={handleIconUpload}
              listType="picture-card"
            >
              {iconUrl ? (
                <img src={iconUrl} alt="图标" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>上传图标</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item
          label="访问URL"
          name="app_url"
          >
            <Input placeholder="应用访问URL（可选）" />
          </Form.Item>

          <Form.Item
          label="描述"
          name="description"
          >
            <Input.TextArea placeholder="应用描述" rows={3} />
          </Form.Item>

          <Form.Item
          label="排序"
          name="sort_order"
          initialValue={0}
          >
            <Input type="number" placeholder="排序数值，数值越小越靠前" />
          </Form.Item>

          {editingApp && (
            <Form.Item
            label="是否启用"
            name="is_active"
            valuePropName="checked"
            initialValue={true}
            >
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}

export default StudentAppsManagement;
